import { createSelector, createSlice, isAnyOf, type PayloadAction } from '@reduxjs/toolkit';
import { isDefined, isNotDefined } from '@sgme/fp';
import type { ColumnState } from 'ag-grid-community';

import { getWorkspaceMatchFulfilled } from '@/api/workspace/index.ts';
import { logger } from '@/components/SgwtBootstrap/logger.ts';
import { blotterApi } from '@/store/blotter/api/index.ts';
import type { BlotterOrderEntryFromBackend } from '@/store/blotter/api/types/BlotterOrderEntryFromBackend.ts';
import { type BlotterTradeEntry, isBlotterTradeInstrument } from '@/store/blotter/api/types/BlotterTradeEntry.ts';
import { mustDiscardLiveNotifications } from '@/store/blotter/discardNotification.ts';
import type { BlotterFilter, BlotterState, BlotterType } from '@/store/blotter/types';
import { convertAccumulatorEntry } from '@/store/blotter/types/BlotterAccumulatorEntry.ts';
import { convertCashEntry } from '@/store/blotter/types/BlotterCashEntry.ts';
import { convertOptionEntry } from '@/store/blotter/types/BlotterOptionEntry.ts';
import { convertOrderEntry } from '@/store/blotter/types/BlotterOrderEntry.ts';
import { convertOrdersForFront } from '@/store/blotter/types/convertOrdersForFront.ts';
import { convertTradesForFront, upsertEntry } from '@/store/blotter/types/convertTradesForFront.ts';

const DEFAULT_BLOTTER_HEIGHT_PX = 300;

const {
  endpoints: { register, getTodayTradesList, getHistoricalTradesList, getActiveOrdersList },
} = blotterApi;

const initialState: BlotterState = {
  status: 'unregistered',
  filter: { type: 'intraday' },
  panelHeight: DEFAULT_BLOTTER_HEIGHT_PX,
  panelIsOpen: false,
  activeTab: 'Cash',
  columnState: {
    Cash: [],
    Accumulator: [],
    Option: [],
    Order: [],
  },
  entries: {
    Cash: {},
    Option: {},
    Accumulator: {},
    Order: {},
  },
};

export const blotterSlice = createSlice({
  name: 'blotter',
  initialState: initialState as BlotterState,
  reducers: {
    filterModified: (state, { payload: filter }: PayloadAction<BlotterFilter>) => {
      state.filter = filter;
    },

    liveTradeReceived: (state, { payload: trades }: PayloadAction<BlotterTradeEntry[]>) => {
      for (const trade of trades) {
        if (!('blotterType' in trade) || !isBlotterTradeInstrument(trade.blotterType)) {
          logger.logError('liveTradeReceived, no blotterType or invalid => ignored : {trade_s}', JSON.stringify(trade));
          continue;
        }

        if (!('id' in trade) || typeof trade.id !== 'string') {
          logger.logError('liveTradeReceived, no id or invalid  => ignored : {trade_s}', JSON.stringify(trade));
          continue;
        }

        switch (trade.blotterType) {
          case 'Cash': {
            const cashEntry = convertCashEntry(trade as BlotterTradeEntry);
            if (isNotDefined(cashEntry) || mustDiscardLiveNotifications(cashEntry.updateTime, state.filter)) {
              continue;
            }
            upsertEntry(cashEntry, state.entries.Cash);
            break;
          }
          case 'Option': {
            const optionEntry = convertOptionEntry(trade as BlotterTradeEntry);
            if (mustDiscardLiveNotifications(optionEntry.updateTime, state.filter)) {
              continue;
            }
            upsertEntry(optionEntry, state.entries.Option);
            break;
          }
          case 'Accumulator': {
            const accumulatorEntry = convertAccumulatorEntry(trade as BlotterTradeEntry);
            if (
              isNotDefined(accumulatorEntry) ||
              mustDiscardLiveNotifications(accumulatorEntry.updateTime, state.filter)
            ) {
              continue;
            }
            upsertEntry(accumulatorEntry, state.entries.Accumulator);
            break;
          }
        }
      }
    },

    liveOrderReceived: (state, { payload: orders }: PayloadAction<BlotterOrderEntryFromBackend[]>) => {
      for (const order of orders) {
        if (isNotDefined(order.id)) {
          logger.logInformation('[ORDER], no id or invalid  => ignored : {order_s}', JSON.stringify(order));
          continue;
        }

        const orderEntry = convertOrderEntry(order);
        upsertEntry(orderEntry, state.entries.Order);
      }
    },

    togglePanel: (state) => {
      state.panelIsOpen = !state.panelIsOpen;
    },

    savePanelHeight: (state, action: PayloadAction<number>) => {
      state.panelHeight = action.payload;
    },

    setActiveTab: (state, action: PayloadAction<BlotterType>) => {
      state.activeTab = action.payload;
    },

    saveCashGridColumnState: (state, action: PayloadAction<ColumnState[]>) => {
      state.columnState.Cash = action.payload;
    },

    saveOrderGridColumnState: (state, action: PayloadAction<ColumnState[]>) => {
      state.columnState.Order = action.payload;
    },

    saveOptionGridColumnState: (state, action: PayloadAction<ColumnState[]>) => {
      state.columnState.Option = action.payload;
    },

    saveAccumulatorGridColumnState: (state, action: PayloadAction<ColumnState[]>) => {
      state.columnState.Accumulator = action.payload;
    },
  },

  extraReducers: (builder) => {
    builder.addMatcher(register.matchPending, (state) => {
      state.status = 'registering';
    });

    builder.addMatcher(register.matchFulfilled, (state) => ({
      ...state,
      status: 'registered',
      impairments: [],
      entries: { Cash: {}, Option: {}, Accumulator: {}, Order: {} },
    }));

    builder.addMatcher(register.matchRejected, (state, action) => ({
      ...state,
      status: 'registration failed',
      reason: action.error,
    }));

    builder.addMatcher(
      isAnyOf(getTodayTradesList.matchFulfilled, getHistoricalTradesList.matchFulfilled),
      (state, { payload: trades }) => {
        if (state.status !== 'registered') {
          console.error('requested todayTradesList while not registered for blotter notifications');
          return;
        }

        // remove related impairment if was present
        const index = state.impairments?.indexOf('initial trades not loaded') ?? -1;
        if (index !== -1) {
          state.impairments?.splice(index, 1);
        }

        const tradeEntries = convertTradesForFront(trades);
        state.entries = { ...state.entries, ...tradeEntries };
      },
    );

    builder.addMatcher(getActiveOrdersList.matchFulfilled, (state, { payload: orders }) => {
      if (state.status !== 'registered') {
        console.error('requested activeOrderList while not registered for blotter notifications');
        return;
      }
      const index = state.impairments?.indexOf('initial orders not loaded') ?? -1;
      if (index !== -1) {
        state.impairments?.splice(index, 1);
      }

      state.entries.Order = convertOrdersForFront(orders);
    });

    builder.addMatcher(isAnyOf(getTodayTradesList.matchRejected, getHistoricalTradesList.matchRejected), (state) => {
      if (state.status !== 'registered') {
        console.error('requested todayTradesList while not registered for blotter notifications');
        return;
      }
      if (!state.impairments?.includes('initial trades not loaded')) {
        state.impairments?.push('initial trades not loaded');
      }
    });

    builder.addMatcher(getActiveOrdersList.matchRejected, (state) => {
      if (state.status !== 'registered') {
        console.error('requested activeOrdersList while not registered for blotter notifications');
        return;
      }
      if (!state.impairments?.includes('initial orders not loaded')) {
        state.impairments?.push('initial orders not loaded');
      }
    });

    ///////////////
    // WORKSPACE //
    ///////////////
    builder.addMatcher(getWorkspaceMatchFulfilled, (state, { payload }) => {
      if (!payload || isNotDefined(payload.blotter)) {
        return;
      }

      if (!state.panelIsOpen) {
        // if the user clicked on the blotter trigger before the blotter state is restored from workspace restoration
        // then the blotter panel would collapse if the blotter state 'isPanelOpen' is false
        // this is the case in blotter e2e tests
        state.panelIsOpen = payload.blotter.panelIsOpen ?? false;
      }

      state.panelHeight = payload.blotter.panelHeight;
      state.activeTab = payload.blotter.activeTab;
      state.columnState.Cash = payload.blotter.cashTab.columnState;
      state.columnState.Accumulator = payload.blotter.accumulatorTab.columnState;
      state.columnState.Option = payload.blotter.optionTab.columnState;
      state.columnState.Order = payload.blotter.orderTab.columnState;
    });
  },

  selectors: {
    checkReady: ({ status }) => status === 'registered',
    getStatus: (state) => state.status,
    getImpairments: (state) => (state.status === 'registered' ? state.impairments : undefined),

    getCountPerType: createSelector(
      (state: BlotterState) => (state.status === 'registered' ? state.entries : undefined),
      (entries) => {
        if (isNotDefined(entries)) {
          return { Cash: 0, Option: 0, Order: 0, Accumulator: 0 };
        }
        return {
          Cash: Object.keys(entries.Cash).length,
          Option: Object.keys(entries.Option).length,
          Order: Object.keys(entries.Order).length,
          Accumulator: Object.keys(entries.Accumulator).length,
        };
      },
    ),

    getCashEntries: createSelector(
      (state: BlotterState) => (state.status === 'registered' ? state.entries.Cash : undefined),
      (map) => (isDefined(map) ? Object.values(map) : []),
    ),

    getCashEntryById: (state: BlotterState, id: string) => {
      if (state.status !== 'registered') {
        return undefined;
      }
      return state.entries.Cash[id];
    },

    getOrderEntries: createSelector(
      (state: BlotterState) => (state.status === 'registered' ? state.entries.Order : undefined),
      (map) => (isDefined(map) ? Object.values(map) : []),
    ),

    getOptionEntries: createSelector(
      (state: BlotterState) => (state.status === 'registered' ? state.entries.Option : undefined),
      (map) => (isDefined(map) ? Object.values(map) : []),
    ),

    getOptionEntryById: (state: BlotterState, id: string) => {
      if (state.status !== 'registered') {
        return undefined;
      }
      return state.entries.Option[id];
    },

    getOrderEntryById: (state: BlotterState, id: string) => {
      if (state.status !== 'registered') {
        return undefined;
      }
      return state.entries.Order[id];
    },

    getOptionEntriesByStrategyReference: (state: BlotterState, strategyReference: string) => {
      if (state.status !== 'registered') {
        return [];
      }
      return Object.values(state.entries.Option).filter((entry) => entry.strategyReference === strategyReference);
    },

    getAccumulatorEntries: createSelector(
      (state: BlotterState) => (state.status === 'registered' ? state.entries.Accumulator : undefined),
      (map) => (isDefined(map) ? Object.values(map) : []),
    ),

    getAccumulatorEntryById: (state: BlotterState, id: string) => {
      if (state.status !== 'registered') {
        return undefined;
      }
      return state.entries.Accumulator[id];
    },

    getFilter: (state) => (state.status === 'registered' ? state.filter : undefined),
    getPanelIsOpen: (state) => state.panelIsOpen,
    getPanelHeight: (state) => state.panelHeight,
    getActiveTab: (state) => state.activeTab,
  },
});
